import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaPrint,
  FaFileExcel,
  FaExclamationTriangle,
  FaDownload,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTitle } from "../../../../context/TitleContext";
import {
  fetchPenaltyTypes,
  deletePenaltyType,
  togglePenaltyTypeStatus,
} from "../../../../redux/Slices/PenaltyTypesSlice";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  getPdfRowStyles,
  toPositiveInteger,
} from "../../../../shared/utils.js/utils";
import { isOnline } from "../../../../shared/utils.js/offlineQueue";
import { useLanguageChange } from "../../../../shared/hooks/useLanguageChange";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";

const PenaltyTypes = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const isRTL = i18n.language === "ar";
  const dir = isRTL ? "rtl" : "ltr";
  const textAlign = isRTL ? "right" : "left";

  const { penaltyTypes, loading } = useSelector(state => state.penaltyTypes);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPenaltyType, setSelectedPenaltyType] = useState(null);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    is_active: "",
  });

  // Client-Side Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);

  // Client-Side Sorting State
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null

  // Local copy of penalty types to perform operations on
  const [rawPenaltyTypes, setRawPenaltyTypes] = useState([]);
  const [isLoadingPenaltyTypes, setIsLoadingPenaltyTypes] = useState(false);

  // PDF Preview & Print State
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewSingleOpen, setIsPreviewSingleOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' | 'all'
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allPenaltyTypesForPdf, setAllPenaltyTypesForPdf] = useState([]);

  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle("sidenav.hr.penaltyTypesNamespace.title");
  }, [setTitle]);

  // Fetch all penalty types from the backend with large page size (1000)
  const fetchAllPenaltyTypes = useCallback(async () => {
    setIsLoadingPenaltyTypes(true);
    try {
      const params = { page: 1, per_page: 1000 };
      await dispatch(fetchPenaltyTypes(params)).unwrap();
    } catch (err) {
      if (err !== "OFFLINE_REQUEST_SAVED") {
        console.error("Failed to fetch penalty types:", err);
      }
    } finally {
      setIsLoadingPenaltyTypes(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAllPenaltyTypes();
  }, [fetchAllPenaltyTypes]);

  useLanguageChange(() => {
    fetchAllPenaltyTypes();
  }, [dispatch]);

  // Sync Redux state updates back to rawPenaltyTypes local state
  useEffect(() => {
    if (penaltyTypes) {
      const list = Array.isArray(penaltyTypes)
        ? penaltyTypes
        : penaltyTypes.data || [];
      setRawPenaltyTypes(list);
    }
  }, [penaltyTypes]);

  // Handle online/offline status changes
  useEffect(() => {
    const handleSync = async event => {
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results) {
        const hasPenaltyTypeRequest = detail.results.some(
          r =>
            r.success === true &&
            r.request?.url?.includes("/penalty-types") &&
            ["POST", "PUT", "PATCH", "DELETE"].includes(r.request?.method),
        );
        if (hasPenaltyTypeRequest && isOnline()) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          fetchAllPenaltyTypes();
        }
      }
    };

    window.addEventListener("offlineRequestsRetried", handleSync);
    return () => {
      window.removeEventListener("offlineRequestsRetried", handleSync);
    };
  }, [fetchAllPenaltyTypes]);

  // 3-State Header-Click Sorting Function
  const handleSort = field => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortField(null);
        setSortOrder(null);
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Render Sorting Icons
  const renderSortIndicator = field => {
    if (sortField !== field) {
      return (
        <span
          style={{ color: "#888", marginInlineStart: "6px", fontSize: "12px" }}
        >
          ⇅
        </span>
      );
    }
    return sortOrder === "asc" ? (
      <span
        style={{ color: "white", marginInlineStart: "6px", fontSize: "12px" }}
      >
        ▲
      </span>
    ) : (
      <span
        style={{ color: "white", marginInlineStart: "6px", fontSize: "12px" }}
      >
        ▼
      </span>
    );
  };

  // Client-Side Search, Filter, and Sort derivation
  const filteredPenaltyTypes = React.useMemo(() => {
    let data = [...rawPenaltyTypes];

    // 1. Search (Arabic / English / Description)
    const query = searchTerm.trim().replace(/\s+/g, " ").toLowerCase();
    if (query) {
      data = data.filter(pt => {
        const nameAr = (pt.name_ar || "").toLowerCase();
        const nameEn = (pt.name_en || "").toLowerCase();
        const desc = (pt.description || "").toLowerCase();
        return (
          nameAr.includes(query) ||
          nameEn.includes(query) ||
          desc.includes(query)
        );
      });
    }

    // 2. Status Filter
    if (filters.is_active !== "") {
      const isActiveFilter = filters.is_active === "1";
      data = data.filter(pt => Boolean(pt.is_active) === isActiveFilter);
    }

    // 3. 3-State Sorting
    if (sortField && sortOrder) {
      data.sort((a, b) => {
        if (sortField === "name") {
          const aVal = isRTL ? a.name_ar || "" : a.name_en || "";
          const bVal = isRTL ? b.name_ar || "" : b.name_en || "";
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal, isRTL ? "ar" : "en", {
                sensitivity: "base",
              })
            : bVal.localeCompare(aVal, isRTL ? "ar" : "en", {
                sensitivity: "base",
              });
        }
        if (sortField === "description") {
          const aVal = a.description || "";
          const bVal = b.description || "";
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal, isRTL ? "ar" : "en", {
                sensitivity: "base",
              })
            : bVal.localeCompare(aVal, isRTL ? "ar" : "en", {
                sensitivity: "base",
              });
        }
        if (sortField === "status") {
          const aVal = a.is_active ? 1 : 0;
          const bVal = b.is_active ? 1 : 0;
          return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
        }
        if (sortField === "created_at") {
          const aVal = new Date(a.created_at || 0).getTime();
          const bVal = new Date(b.created_at || 0).getTime();
          return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
        }
        return 0;
      });
    }

    return data;
  }, [rawPenaltyTypes, searchTerm, filters, sortField, sortOrder, isRTL]);

  // Reset pagination to page 1 on search or filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPenaltyTypes.length / perPage),
  );
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedPenaltyTypes = filteredPenaltyTypes.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );

  const handleApplyPerPage = () => {
    const safeValue = toPositiveInteger(perPageInput, perPage);
    setPerPageInput(safeValue);
    if (safeValue !== perPage) {
      setPerPage(safeValue);
      setCurrentPage(1);
    }
  };

  const handleAddNew = () => {
    navigate("/penalty-types/add");
  };

  const handleEdit = penaltyType => {
    navigate(`/penalty-types/edit/${penaltyType.id}`);
  };

  const handleDeleteClick = penaltyType => {
    setSelectedPenaltyType(penaltyType);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPenaltyType) return;
    const backup = selectedPenaltyType;
    // Optimistic delete
    setRawPenaltyTypes(prev => prev.filter(pt => pt.id !== backup.id));
    setShowDeleteModal(false);
    setSelectedPenaltyType(null);

    try {
      const result = await dispatch(deletePenaltyType(backup.id)).unwrap();
      if (result && result.isOffline) {
        localStorage.setItem("offline_add_performed", "true");
        toast.success(
          isRTL
            ? "تم حذف نوع الجزاء محلياً وسيتم المزامنة عند عودة الاتصال"
            : "Penalty type deleted locally and will sync when online",
        );
      } else {
        toast.success(t("sidenav.hr.penaltyTypesNamespace.deleteSuccess"));
      }
      fetchAllPenaltyTypes();
    } catch (error) {
      setRawPenaltyTypes(prev => [...prev, backup].sort((a, b) => a.id - b.id));
      toast.error(
        error?.message || t("sidenav.hr.penaltyTypesNamespace.errorDeleting"),
      );
    }
  };

  const handleToggleStatus = async penaltyType => {
    try {
      const result = await dispatch(
        togglePenaltyTypeStatus(penaltyType.id),
      ).unwrap();
      if (result && result.isOffline) {
        localStorage.setItem("offline_add_performed", "true");
        toast.success(
          isRTL
            ? "تم تغيير الحالة محلياً وسيتم المزامنة عند عودة الاتصال"
            : "Status toggled locally and will sync when online",
        );
      } else {
        toast.success(t("messages.update_success"));
      }
    } catch (error) {
      toast.error(
        error?.message || t("sidenav.hr.penaltyTypesNamespace.errorUpdating"),
      );
    }
  };

  // --- Dynamic Chunking (PDF Pagination Engine) ---

  const colStyles = {
    serial: {
      width: "8%",
      textAlign: "center",
      verticalAlign: "middle",
      border: "1px solid #000",
      padding: "6px 2px",
      fontSize: "10px",
    },
    name: {
      width: "25%",
      textAlign: isRTL ? "right" : "left",
      verticalAlign: "middle",
      border: "1px solid #000",
      padding: "6px 2px",
      fontSize: "10px",
      wordBreak: "break-word",
    },
    description: {
      width: "35%",
      textAlign: isRTL ? "right" : "left",
      verticalAlign: "middle",
      border: "1px solid #000",
      padding: "6px 2px",
      fontSize: "10px",
      wordBreak: "break-word",
    },
    status: {
      width: "15%",
      textAlign: "center",
      verticalAlign: "middle",
      border: "1px solid #000",
      padding: "6px 2px",
      fontSize: "10px",
    },
    created: {
      width: "17%",
      textAlign: "center",
      verticalAlign: "middle",
      border: "1px solid #000",
      padding: "6px 2px",
      fontSize: "10px",
    },
  };

  const calculateDynamicChunks = items => {
    const tableContainer = document.getElementById(
      "penalty-types-measurement-container",
    );
    const thead = tableContainer ? tableContainer.querySelector("thead") : null;
    const theadHeight = thead ? thead.offsetHeight : 35;
    const rows = tableContainer
      ? tableContainer.querySelectorAll("tbody tr")
      : [];

    const MAX_TABLE_HEIGHT = 740;
    const chunks = [];
    let currentChunk = [];
    let currentHeight = theadHeight;

    items.forEach((item, index) => {
      const rowElement = rows[index];
      let rowHeight = 45; // Default fallback height

      if (rowElement && rowElement.offsetHeight > 0) {
        rowHeight = rowElement.offsetHeight;
      } else {
        // Fallback calculations in case elements aren't rendered
        const nameLen = isRTL
          ? (item.name_ar || "").length
          : (item.name_en || "").length;
        const descLen = (item.description || "").length;
        const maxTextLen = Math.max(nameLen / 30, descLen / 45);
        const lines = Math.max(1, Math.ceil(maxTextLen));
        rowHeight = 35 + lines * 15;
      }

      if (
        currentHeight + rowHeight > MAX_TABLE_HEIGHT &&
        currentChunk.length > 0
      ) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentHeight = theadHeight;
      }
      currentChunk.push(item);
      currentHeight += rowHeight;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    return chunks;
  };

  // --- Preview & Print Actions ---

  const handlePreviewSingle = penaltyType => {
    setSelectedPenaltyType(penaltyType);
    setIsPreviewSingleOpen(true);
  };

  const handlePreviewAll = async () => {
    if (!filteredPenaltyTypes || filteredPenaltyTypes.length === 0) {
      toast.error(
        t("sidenav.hr.penaltyTypesNamespace.noData", {
          defaultValue: "لا توجد بيانات للمعاينة",
        }),
        { rtl: isRTL },
      );
      return;
    }
    setAllPenaltyTypesForPdf(filteredPenaltyTypes);
    await new Promise(resolve => setTimeout(resolve, 300));
    const chunks = calculateDynamicChunks(filteredPenaltyTypes);
    setPdfChunks(chunks);
    setIsPreviewOpen(true);
  };

  const handlePrintSingle = async penaltyType => {
    try {
      setSelectedPenaltyType(penaltyType);
      setPdfType("single");
      await new Promise(resolve => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));

      setIsGeneratingPdf(true);

      const element = document.getElementById(
        `penalty-type-pdf-${penaltyType.id}`,
      );
      if (!element) {
        toast.error(
          t("sidenav.receiptVouchersMenu.print.error", {
            defaultValue: "خطأ في تحميل قالب الطباعة",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById(
            `penalty-type-pdf-${penaltyType.id}`,
          );
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction = isRTL ? "rtl" : "ltr";
          }
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgData = canvas.toDataURL("image/png", 1.0);
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(
        imgData,
        "PNG",
        0,
        0,
        imgWidth,
        imgHeight,
        undefined,
        "FAST",
      );

      const identifier = isRTL
        ? penaltyType.name_ar || penaltyType.id
        : penaltyType.name_en || penaltyType.id;
      pdf.save(
        `ACE_PenaltyType_${identifier}_${new Date().toISOString().split("T")[0]}.pdf`,
      );
      toast.success(
        t("sidenav.receiptVouchersMenu.print.success", {
          defaultValue: "تم تنزيل PDF بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (err) {
      console.error("Penalty Type PDF error:", err);
      toast.error(
        t("sidenav.receiptVouchersMenu.print.error", {
          defaultValue: "حدث خطأ أثناء طباعة PDF",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setSelectedPenaltyType(null);
      setPdfType(null);
    }
  };

  const handlePrintAll = async () => {
    if (!filteredPenaltyTypes || filteredPenaltyTypes.length === 0) {
      toast.error(
        t("sidenav.hr.penaltyTypesNamespace.noData", {
          defaultValue: "لا توجد بيانات للطباعة",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      setAllPenaltyTypesForPdf(filteredPenaltyTypes);
      await new Promise(resolve => setTimeout(resolve, 300));

      const chunks = calculateDynamicChunks(filteredPenaltyTypes);
      setPdfChunks(chunks);
      setPdfType("all");

      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 100));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      setIsGeneratingPdf(true);

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => setTimeout(resolve, 100));

        const element = document.getElementById(
          `penalty-types-pdf-all-${chunkIndex}`,
        );
        if (!element) {
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `penalty-types-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      }

      pdf.save(
        `ACE_PenaltyTypes_${new Date().toISOString().split("T")[0]}.pdf`,
      );
      toast.success(
        t("sidenav.receiptVouchersMenu.print.success", {
          defaultValue: "تم تنزيل PDF بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Penalty Types PDF error:", error);
      toast.error(
        t("sidenav.receiptVouchersMenu.print.error", {
          defaultValue: "حدث خطأ أثناء طباعة PDF",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // --- Excel Export ---
  const handleExportExcel = async () => {
    if (!filteredPenaltyTypes || filteredPenaltyTypes.length === 0) {
      toast.error(
        t("sidenav.hr.penaltyTypesNamespace.noData", {
          defaultValue: "لا توجد بيانات للتصدير",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      const data = filteredPenaltyTypes.map((pt, idx) => ({
        [t("common.serialNumber", { defaultValue: "#" })]: idx + 1,
        [isRTL
          ? t("nameAr", { defaultValue: "الاسم بالعربية" })
          : t("nameAr", { defaultValue: "Arabic Name" })]: pt.name_ar || "—",
        [isRTL
          ? t("nameEn", { defaultValue: "الاسم بالإنجليزية" })
          : t("nameEn", { defaultValue: "English Name" })]: pt.name_en || "—",
        [t("description", { defaultValue: "الوصف" })]: pt.description || "—",
        [t("status", { defaultValue: "الحالة" })]: pt.is_active
          ? t("active", { defaultValue: "نشط" })
          : t("inactive", { defaultValue: "غير نشط" }),
        [t("createdAt", { defaultValue: "تاريخ الإنشاء" })]: pt.created_at
          ? new Date(pt.created_at).toLocaleDateString("en-US")
          : "—",
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        isRTL ? "أنواع العقوبات" : "Penalty Types",
      );
      const fileName = `${isRTL ? "أنواع_العقوبات" : "Penalty_Types"}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("sidenav.notesReceivable.export.success", {
          defaultValue: "تم التصدير بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      toast.error(
        error?.message ||
          t("sidenav.notesReceivable.export.error", {
            defaultValue: "حدث خطأ أثناء تصدير البيانات",
          }),
        { rtl: isRTL },
      );
    }
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  // Render Multi-page Dynamic PDF Page Template
  const renderPdfPage = (chunk, chunkIdx, totalPages, elementId = null) => {
    const hasActiveFilters = searchTerm.trim() || filters.is_active !== "";
    const previewFormatDate = ds =>
      ds ? new Date(ds).toLocaleDateString("en-US") : "--";

    return (
      <div
        id={elementId}
        style={{
          width: "794px",
          height: "1123px",
          backgroundColor: "#ffffff",
          color: "#000000",
          fontFamily:
            '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: "bold",
          padding: "40px",
          boxSizing: "border-box",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            border: "5px solid #000",
            padding: "3px",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            height: "1043px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              border: "2px solid #000",
              height: "1033px",
              padding: "18px 18px 10px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              textRendering: "optimizeLegibility",
              direction: isRTL ? "rtl" : "ltr",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: "60%",
                backgroundPosition: "center top",
                backgroundRepeat: "no-repeat",
                opacity: 0.05,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                  marginBottom: "8px",
                  borderBottom: "4px double #000",
                  paddingBottom: "8px",
                  display: "block",
                  flexShrink: 0,
                }}
                crossOrigin="anonymous"
              />

              <div
                style={{
                  textAlign: "center",
                  padding: "6px 0 8px",
                  borderBottom: "1px solid #ddd",
                  marginBottom: "4px",
                  flexShrink: 0,
                  direction: isRTL ? "rtl" : "ltr",
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "900",
                    color: "#000080",
                  }}
                >
                  {isRTL ? "تقرير أنواع العقوبات" : "Penalty Types Report"}
                </div>
                <div
                  style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}
                >
                  {isRTL ? "تاريخ التقرير" : "Report Date"}:{" "}
                  {new Date().toLocaleDateString("en-US")} —{" "}
                  {isRTL ? "صفحة" : "Page"} {chunkIdx + 1} / {totalPages}
                </div>
                {hasActiveFilters && (
                  <div
                    style={{
                      fontSize: "9px",
                      color: "#444",
                      marginTop: "6px",
                      display: "flex",
                      gap: "12px",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      backgroundColor: "rgba(238, 242, 247, 0.5)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    {searchTerm.trim() && (
                      <span>
                        {t("common.search", { defaultValue: "بحث" })}:{" "}
                        {searchTerm.trim().replace(/\s+/g, " ")}
                      </span>
                    )}
                    {filters.is_active !== "" && (
                      <span>
                        {t("status", { defaultValue: "الحالة" })}:{" "}
                        {filters.is_active === "1"
                          ? t("active", { defaultValue: "نشط" })
                          : t("inactive", { defaultValue: "غير نشط" })}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div
                style={{
                  marginTop: "12px",
                  direction: isRTL ? "rtl" : "ltr",
                  fontFamily: '"Times New Roman", "Times", serif',
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    border: "1px solid #000",
                    fontFamily: '"Times New Roman", "Times", serif',
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "25%" }} />
                    <col style={{ width: "35%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "17%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}>
                      <th style={colStyles.serial}>#</th>
                      <th style={colStyles.name}>
                        {isRTL ? t("nameAr") : t("nameEn")}
                      </th>
                      <th style={colStyles.description}>{t("description")}</th>
                      <th style={colStyles.status}>{t("status")}</th>
                      <th style={colStyles.created}>{t("createdAt")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk && chunk.length > 0 ? (
                      chunk.map((item, idx) => {
                        const foundIdx = allPenaltyTypesForPdf.findIndex(
                          pt => String(pt?.id) === String(item?.id),
                        );
                        const serial =
                          foundIdx >= 0
                            ? foundIdx + 1
                            : chunkIdx * 25 + idx + 1;
                        const cellBgColor =
                          idx % 2 === 0
                            ? "rgba(255, 255, 255, 0.7)"
                            : "rgba(245, 245, 245, 0.5)";
                        return (
                          <tr
                            key={item.id}
                            style={{ backgroundColor: cellBgColor }}
                          >
                            <td
                              style={{
                                ...colStyles.serial,
                                backgroundColor: cellBgColor,
                              }}
                            >
                              {serial}
                            </td>
                            <td
                              style={{
                                ...colStyles.name,
                                backgroundColor: cellBgColor,
                              }}
                            >
                              {isRTL ? item.name_ar : item.name_en}
                            </td>
                            <td
                              style={{
                                ...colStyles.description,
                                backgroundColor: cellBgColor,
                              }}
                            >
                              {item.description || "—"}
                            </td>
                            <td
                              style={{
                                ...colStyles.status,
                                backgroundColor: cellBgColor,
                                color: item.is_active ? "#28a745" : "#dc3545",
                              }}
                            >
                              {item.is_active ? t("active") : t("inactive")}
                            </td>
                            <td
                              style={{
                                ...colStyles.created,
                                backgroundColor: cellBgColor,
                              }}
                            >
                              {previewFormatDate(item.created_at)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="5"
                          style={{
                            border: "1px solid #000",
                            padding: "15px",
                            textAlign: "center",
                            color: "#666",
                            fontSize: "12px",
                          }}
                        >
                          {t("sidenav.hr.penaltyTypesNamespace.noData", {
                            defaultValue: "لا توجد بيانات",
                          })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  position: "absolute",
                  bottom: "90px",
                  [isRTL ? "left" : "right"]: "24px",
                  fontSize: "10px",
                  color: "#666",
                  fontWeight: "normal",
                  direction: isRTL ? "rtl" : "ltr",
                  zIndex: 10,
                }}
              >
                {isRTL
                  ? `صفحة ${chunkIdx + 1} من ${totalPages}`
                  : `Page ${chunkIdx + 1} of ${totalPages}`}
              </div>

              <div
                style={{
                  position: "absolute",
                  bottom: "10px",
                  left: "18px",
                  right: "18px",
                  flexShrink: 0,
                }}
              >
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                      borderTop: "4px double #000",
                      paddingTop: "8px",
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Single-page Dynamic PDF Page Template
  const renderSinglePdfPage = (penaltyType, isPreview = false) => {
    const containerId = isPreview
      ? `preview-penalty-type-pdf-${penaltyType.id}`
      : `penalty-type-pdf-${penaltyType.id}`;
    const previewFormatDate = ds =>
      ds ? new Date(ds).toLocaleDateString("en-US") : "--";

    return (
      <div
        id={containerId}
        style={{
          width: "794px",
          height: "1123px",
          backgroundColor: "#ffffff",
          color: "#000000",
          fontFamily:
            '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: "bold",
          padding: "40px",
          boxSizing: "border-box",
          position: isPreview ? "relative" : "absolute",
          left: isPreview ? "0" : "-9999px",
          top: 0,
          boxShadow: isPreview ? "0 4px 20px rgba(0, 0, 0, 0.15)" : "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            border: "5px solid #000",
            padding: "3px",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            height: "1043px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              border: "2px solid #000",
              height: "1033px",
              padding: "18px 18px 10px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              direction: isRTL ? "rtl" : "ltr",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: "95%",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                  marginBottom: "8px",
                  borderBottom: "4px double #000",
                  paddingBottom: "8px",
                  flexShrink: 0,
                  display: "block",
                }}
                crossOrigin="anonymous"
              />

              <div
                style={{
                  textAlign: "center",
                  padding: "6px 0 8px",
                  borderBottom: "1px solid #ddd",
                  marginBottom: "4px",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "900",
                    color: "#000080",
                  }}
                >
                  {isRTL ? "تفاصيل نوع العقوبة" : "Penalty Type Details"}
                </div>
                <div
                  style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}
                >
                  ID: #{penaltyType.id}
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  paddingBottom: "120px",
                }}
              >
                <div
                  style={{
                    marginTop: "12px",
                    border: "2px solid #000",
                    borderRadius: "4px",
                    overflow: "hidden",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                  }}
                >
                  {[
                    {
                      label: isRTL
                        ? t("nameAr", { defaultValue: "الاسم بالعربية" })
                        : t("nameAr", { defaultValue: "Arabic Name" }),
                      value: penaltyType.name_ar || "—",
                    },
                    {
                      label: isRTL
                        ? t("nameEn", { defaultValue: "الاسم بالإنجليزية" })
                        : t("nameEn", { defaultValue: "English Name" }),
                      value: penaltyType.name_en || "—",
                    },
                    {
                      label: t("description", { defaultValue: "الوصف" }),
                      value: penaltyType.description || "—",
                    },
                    {
                      label: t("status", { defaultValue: "الحالة" }),
                      value: penaltyType.is_active
                        ? t("active", { defaultValue: "نشط" })
                        : t("inactive", { defaultValue: "غير نشط" }),
                      accent: true,
                    },
                    {
                      label: t("createdAt", { defaultValue: "تاريخ الإنشاء" }),
                      value: previewFormatDate(penaltyType.created_at) || "—",
                    },
                  ].map((row, idx) => {
                    const pdfStyles = getPdfRowStyles(isRTL);
                    return (
                      <div
                        key={idx}
                        style={{
                          ...pdfStyles.row,
                          backgroundColor:
                            idx % 2 === 0 ? "rgba(0,0,0,0.03)" : "transparent",
                          borderBottom: idx === 4 ? "none" : "1px solid #000",
                        }}
                      >
                        {isRTL ? (
                          <>
                            <div
                              style={{
                                ...pdfStyles.value,
                                color: row.accent
                                  ? penaltyType.is_active
                                    ? "#28a745"
                                    : "#dc3545"
                                  : "#000",
                              }}
                            >
                              {row.value}
                            </div>
                            <div style={{ ...pdfStyles.label, color: "#000" }}>
                              {row.label}
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ ...pdfStyles.label, color: "#000" }}>
                              {row.label}
                            </div>
                            <div
                              style={{
                                ...pdfStyles.value,
                                color: row.accent
                                  ? penaltyType.is_active
                                    ? "#28a745"
                                    : "#dc3545"
                                  : "#000",
                              }}
                            >
                              {row.value}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div
                style={{
                  position: "absolute",
                  bottom: "90px",
                  [isRTL ? "left" : "right"]: "24px",
                  fontSize: "10px",
                  color: "#666",
                  fontWeight: "normal",
                  direction: isRTL ? "rtl" : "ltr",
                  zIndex: 10,
                }}
              >
                {isRTL ? "صفحة 1 من 1" : "Page 1 of 1"}
              </div>

              <div
                style={{
                  position: "absolute",
                  bottom: "10px",
                  left: "18px",
                  right: "18px",
                  flexShrink: 0,
                }}
              >
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: "100%",
                    height: "auto",
                      borderTop: "4px double #000",
                      paddingTop: "8px",
                    objectFit: "contain",
                    display: "block",
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if ((loading || isLoadingPenaltyTypes) && rawPenaltyTypes.length === 0) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {t("common.loading", { defaultValue: "جاري التحميل..." })}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      {/* Title & Actions */}
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            marginBottom: "20px",
            color: "white",
            textAlign,
          }}
        >
          {t("sidenav.hr.penaltyTypes")}
        </h1>

        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          {/* Search Box */}
          <div style={{ position: "relative", flex: "1", minWidth: "300px" }}>
            <FaSearch
              style={{
                position: "absolute",
                [isRTL ? "right" : "left"]: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
                fontSize: "16px",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder={t(
                "sidenav.hr.penaltyTypesNamespace.searchPlaceholder",
              )}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: isRTL ? "12px 45px 12px 15px" : "12px 15px 12px 45px",
                backgroundColor: "#1b3350",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                textAlign: isRTL ? "right" : "left",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Status Filter */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label
              style={{ color: "white", fontSize: "14px", whiteSpace: "nowrap" }}
            >
              {t("status")}:
            </label>
            <select
              value={filters.is_active}
              onChange={e => {
                const nextStatus = e.target.value;
                setFilters(prev => ({ ...prev, is_active: nextStatus }));
              }}
              style={{
                padding: "12px 15px",
                backgroundColor: "#1b3350",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                cursor: "pointer",
                minWidth: "120px",
              }}
            >
              <option value="">{t("all")}</option>
              <option value="1">{t("active")}</option>
              <option value="0">{t("inactive")}</option>
            </select>
          </div>

          {/* Per Page Selector */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label
              style={{ color: "white", fontSize: "14px", whiteSpace: "nowrap" }}
            >
              {t("sidenav.hr.penaltyTypesNamespace.perPage")}:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onClick={() =>
                  setPerPageInput(
                    Math.max(1, toPositiveInteger(perPageInput, perPage) - 1),
                  )
                }
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "6px 0 0 6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  minWidth: "32px",
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                step="1"
                value={perPageInput}
                onChange={e =>
                  setPerPageInput(toPositiveInteger(e.target.value, perPage))
                }
                style={{
                  width: "70px",
                  padding: "6px 10px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderLeft: "none",
                  borderRight: "none",
                  borderRadius: 0,
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              />
              <button
                onClick={() =>
                  setPerPageInput(toPositiveInteger(perPageInput, perPage) + 1)
                }
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "0 6px 6px 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  minWidth: "32px",
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
              }}
            >
              {t("sidenav.hr.penaltyTypesNamespace.apply")}
            </button>
          </div>

          {/* Action Buttons */}
          <button
            onClick={handleAddNew}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaPlus />
            {t("btns.add")}
          </button>

          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaEye />
            {t("btns.viewAll", { defaultValue: "معاينة PDF للكل" })}
          </button>

          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: isGeneratingPdf ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              opacity: isGeneratingPdf ? 0.6 : 1,
            }}
          >
            <FaPrint />
            {isGeneratingPdf
              ? t("common.generating", { defaultValue: "جاري الإنشاء..." })
              : t("sidenav.hr.penaltyTypesNamespace.printAll", {
                  defaultValue: "طباعة PDF للكل",
                })}
          </button>

          <button
            onClick={handleExportExcel}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaFileExcel />
            {t("sidenav.hr.penaltyTypesNamespace.exportAll", {
              defaultValue: "تصدير Excel للكل",
            })}
          </button>
        </div>
      </div>

      {/* Main Table */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #333",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: "1000px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  #
                </th>
                <th
                  onClick={() => handleSort("name")}
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  {isRTL ? t("nameAr") : t("nameEn")}{" "}
                  {renderSortIndicator("name")}
                </th>
                <th
                  onClick={() => handleSort("description")}
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  {t("description")} {renderSortIndicator("description")}
                </th>
                <th
                  onClick={() => handleSort("status")}
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  {t("status")} {renderSortIndicator("status")}
                </th>
                <th
                  onClick={() => handleSort("created_at")}
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  {t("createdAt")} {renderSortIndicator("created_at")}
                </th>
                <th
                  style={{
                    padding: "18px 16px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  {t("btns.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedPenaltyTypes && paginatedPenaltyTypes.length > 0 ? (
                paginatedPenaltyTypes.map((penaltyType, idx) => {
                  const rowNumber =
                    sortOrder === "desc"
                      ? filteredPenaltyTypes.length -
                        ((currentPageSafe - 1) * perPage + idx)
                      : (currentPageSafe - 1) * perPage + idx + 1;
                  return (
                    <tr
                      key={penaltyType.id}
                      style={{ borderBottom: "1px solid #333" }}
                    >
                      <td
                        style={{
                          padding: "18px 16px",
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        {rowNumber}
                      </td>
                      <td
                        style={{
                          padding: "18px 16px",
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        {isRTL ? penaltyType.name_ar : penaltyType.name_en}
                      </td>
                      <td
                        style={{
                          padding: "18px 16px",
                          color: "#9ca3af",
                          textAlign: "center",
                          maxWidth: "300px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {penaltyType.description || "-"}
                      </td>
                      <td style={{ padding: "18px 16px", textAlign: "center" }}>
                        <span
                          style={{
                            backgroundColor: penaltyType.is_active
                              ? "#AC2000"
                              : "#dc3545",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "20px",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          {penaltyType.is_active ? t("active") : t("inactive")}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "18px 16px",
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        {new Date(penaltyType.created_at).toLocaleDateString(
                          "en-US",
                        )}
                      </td>
                      <td style={{ padding: "18px 16px" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={() => handlePreviewSingle(penaltyType)}
                            style={{
                              backgroundColor: "#17a2b8",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                            title={t("btns.view")}
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => handleEdit(penaltyType)}
                            style={{
                              backgroundColor: "#AC2000",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(penaltyType)}
                            style={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                          >
                            <FaTrash />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(penaltyType)}
                            style={{
                              backgroundColor: "#34495e",
                              color: "white",
                              border: "none",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                          >
                            {penaltyType.is_active ? (
                              <FaToggleOn />
                            ) : (
                              <FaToggleOff />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    <FaExclamationTriangle
                      style={{ fontSize: "48px", marginBottom: "16px" }}
                    />
                    <div>{t("sidenav.hr.penaltyTypesNamespace.noData")}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Client-Side Pagination Controls */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginTop: "20px",
              paddingBottom: "20px",
            }}
          >
            <button
              onClick={() => handlePageChange(Math.max(1, currentPageSafe - 1))}
              disabled={currentPageSafe === 1}
              style={{
                padding: "8px 12px",
                backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
              }}
            >
              {t("btns.previous", {
                defaultValue: isRTL ? "السابق" : "Previous",
              })}
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                page =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPageSafe - 2 && page <= currentPageSafe + 2),
              )
              .map((page, idx, arr) => {
                if (idx > 0 && page - arr[idx - 1] > 1) {
                  return (
                    <React.Fragment key={`ellipsis-${page}`}>
                      <span style={{ padding: "8px", color: "white" }}>
                        ...
                      </span>
                      <button
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: "8px 12px",
                          backgroundColor:
                            page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                          color: "white",
                          border: "1px solid #333",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                }
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      padding: "8px 12px",
                      backgroundColor:
                        page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                      color: "white",
                      border: "1px solid #333",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {page}
                  </button>
                );
              })}
            <button
              onClick={() =>
                handlePageChange(Math.min(totalPages, currentPageSafe + 1))
              }
              disabled={currentPageSafe === totalPages}
              style={{
                padding: "8px 12px",
                backgroundColor:
                  currentPageSafe === totalPages ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor:
                  currentPageSafe === totalPages ? "not-allowed" : "pointer",
              }}
            >
              {t("btns.next", { defaultValue: isRTL ? "التالي" : "Next" })}
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal (Portal) */}
      {showDeleteModal &&
        selectedPenaltyType &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 999999,
            }}
            onClick={() => setShowDeleteModal(false)}
          >
            <div
              style={{
                backgroundColor: "#1a1f2e",
                borderRadius: "8px",
                padding: "30px",
                maxWidth: "500px",
                width: "90%",
                direction: dir,
                textAlign: textAlign,
                border: "1px solid #333",
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "20px",
                  color: "white",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  fontSize: "18px",
                }}
              >
                {t("sidenav.hr.penaltyTypesNamespace.confirmDelete")}
              </h3>
              <p
                style={{
                  color: "white",
                  marginBottom: "20px",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontSize: "16px",
                }}
              >
                {t("btns.confirmDelete")}:{" "}
                <strong style={{ color: "#AC2000" }}>
                  {isRTL
                    ? selectedPenaltyType.name_ar
                    : selectedPenaltyType.name_en}
                </strong>
                ?
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    padding: "12px 25px",
                    backgroundColor: "#666",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {t("btns.cancel")}
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  style={{
                    padding: "12px 25px",
                    backgroundColor: "#f44336",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {t("btns.delete")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Standard PDF Preview Modal for All Penalty Types */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={t("sidenav.hr.penaltyTypes", { defaultValue: "أنواع العقوبات" })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfChunks([]);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {pdfChunks &&
            pdfChunks.length > 0 &&
            pdfChunks.map((chunk, chunkIdx) => (
              <div
                key={chunkIdx}
                style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)" }}
              >
                {renderPdfPage(chunk, chunkIdx, pdfChunks.length)}
              </div>
            ))}
        </div>
      </PdfPreviewModal>

      {/* Standard PDF Preview Modal for Single Penalty Type */}
      <PdfPreviewModal
        isOpen={isPreviewSingleOpen && selectedPenaltyType}
        title={isRTL ? "تفاصيل نوع العقوبة" : "Penalty Type Details"}
        onClose={() => {
          setIsPreviewSingleOpen(false);
          setSelectedPenaltyType(null);
        }}
        onPrint={() => handlePrintSingle(selectedPenaltyType)}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          {selectedPenaltyType &&
            renderSinglePdfPage(selectedPenaltyType, true)}
        </div>
      </PdfPreviewModal>

      {/* Hidden Templates for Print Rendering */}
      {pdfType === "single" && selectedPenaltyType && (
        <div
          id={`penalty-type-pdf-${selectedPenaltyType.id}`}
          style={{ position: "absolute", left: "-9999px", top: 0 }}
        >
          {renderSinglePdfPage(selectedPenaltyType, false)}
        </div>
      )}
      {pdfType === "all" && pdfChunks && pdfChunks.length > 0 && (
        <>
          {pdfChunks.map((chunk, chunkIndex) => (
            <div
              key={chunkIndex}
              style={{
                position: "absolute",
                left: "-9999px",
                top: 0,
              }}
            >
              {renderPdfPage(
                chunk,
                chunkIndex,
                pdfChunks.length,
                `penalty-types-pdf-all-${chunkIndex}`,
              )}
            </div>
          ))}
        </>
      )}

      {/* Hidden Measurement Table for dynamic heights pagination */}
      <div
        id="penalty-types-measurement-container"
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "714px",
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #000",
            fontFamily: '"Times New Roman", "Times", serif',
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "8%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "35%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "17%" }} />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: "rgba(238,242,247,0.7)" }}>
              <th style={colStyles.serial}>#</th>
              <th style={colStyles.name}>
                {isRTL ? t("nameAr") : t("nameEn")}
              </th>
              <th style={colStyles.description}>{t("description")}</th>
              <th style={colStyles.status}>{t("status")}</th>
              <th style={colStyles.created}>{t("createdAt")}</th>
            </tr>
          </thead>
          <tbody>
            {allPenaltyTypesForPdf.map((pt, idx) => (
              <tr key={pt.id}>
                <td style={colStyles.serial}>{idx + 1}</td>
                <td
                  style={{
                    ...colStyles.name,
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  {isRTL ? pt.name_ar : pt.name_en}
                </td>
                <td
                  style={{
                    ...colStyles.description,
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  {pt.description || "—"}
                </td>
                <td style={colStyles.status}>
                  {pt.is_active ? t("active") : t("inactive")}
                </td>
                <td style={colStyles.created}>
                  {pt.created_at
                    ? new Date(pt.created_at).toLocaleDateString("en-US")
                    : "--"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PenaltyTypes;
